import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import type { Sport } from '@/hooks/useSport'
import { SportToggle } from '@/components/ui/SportToggle'
import { useTenant } from '@/contexts/TenantContext'

interface NavItem {
  label: string
  path: string
}

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  sport: Sport
  onToggleSport: () => void
  navItems: NavItem[]
}

export function MobileMenu({ isOpen, onClose, sport, onToggleSport, navItems }: MobileMenuProps) {
  const location = useLocation()
  const tenant = useTenant()

  useEffect(() => { onClose() }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  return (
    <>
      {isOpen && <div className="menu-overlay animate-fade-in" onClick={onClose} />}

      <div className={clsx(
        'fixed top-0 left-0 bottom-0 w-72 bg-pitch-900 border-r border-pitch-700 z-50',
        'transition-transform duration-300 ease-out',
        isOpen ? 'translate-x-0' : '-translate-x-full',
      )}>
        <div className="p-5 space-y-6">
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-white">{tenant.brandName}</span>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <SportToggle sport={sport} onToggle={onToggleSport} />

          <nav className="space-y-1">
            {navItems.map((item) => {
              const href = item.path === '/recruiting-toolkit' ? item.path : `/${sport}${item.path}`
              const isActive = location.pathname === href
              return (
                <Link
                  key={item.label}
                  to={href}
                  className={clsx(
                    'block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive ? 'bg-accent text-white' : 'text-slate-300 hover:bg-pitch-800',
                  )}
                >
                  {item.label}
                </Link>
              )
            })}
          </nav>
        </div>
      </div>
    </>
  )
}
